import { useCallback, useEffect, useRef, useState } from 'react';

import { setStudentPaymentPermissionHandler } from '@/lib/studentPaymentPermissionGate';

import { StudentPaymentPermissionForm } from './StudentPaymentPermissionForm';

// Opens the one-time payment permission step in Learn
// (student-self-signup-prd.md §19, D2). Mounted once in the Learn shell.
//
// Every student checkout route answers 403 STUDENT_PAYMENT_PERMISSION_REQUIRED
// until the student confirms. `api()` hands that refusal to this host
// (studentPaymentPermissionGate.ts), which shows the step over the page the
// student is on: a top-up, a class seat, tutoring, Academy, Study or a
// Challenge entry. On "Confirm and continue" the same request is sent again and
// the purchase goes on. "Not now" closes the step and the page shows the
// refusal as its normal error.
//
// The refusal comes before anything is bought, so nothing is charged twice.

export function StudentPaymentPermissionHost() {
  const [open, setOpen] = useState(false);
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);

  const finish = useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setOpen(false);
  }, []);

  useEffect(() => {
    const unregister = setStudentPaymentPermissionHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolver.current = resolve;
          setOpen(true);
        }),
    );
    return () => {
      unregister();
      // A request still waiting must not hang when Learn unmounts.
      resolver.current?.(false);
      resolver.current = null;
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open, finish]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-ink/60 p-0 sm:items-center sm:p-6"
      role="presentation"
      data-testid="payment-permission-dialog"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-permission-title"
        className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-canvas-pure p-5 shadow-2xl sm:max-w-[560px] sm:rounded-2xl sm:p-7"
      >
        <span className="eyebrow eyebrow-sky">Before you buy</span>
        <h2
          id="payment-permission-title"
          className="mt-2 text-[22px] font-bold leading-tight text-ink sm:text-[26px]"
        >
          Are you allowed to pay?
        </h2>
        <div className="mt-4">
          <StudentPaymentPermissionForm
            onConfirmed={() => finish(true)}
            onCancel={() => finish(false)}
          />
        </div>
      </div>
    </div>
  );
}
